import { Fragment } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { BackrsTextField } from '@backrs_ui/common-ui/src/atoms/BackrsTextField';

import { Box, Button, Divider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import Lottie from 'lottie-react';
import { useForm, FormProvider, Controller, UseFormReturn } from 'react-hook-form';

import { BackrsFormField, BackrsFormValues } from './types';

type BackrsFormProps = Readonly<{
  fields: BackrsFormField[];
  onSubmit: (data: BackrsFormValues) => void;
  defaultValues?: BackrsFormValues;
  buttonLabel?: string;
  children?: React.ReactNode;
  methods?: UseFormReturn<BackrsFormValues>;
  isLoading?: boolean;
}>;

export const BackrsForm = ({
  fields,
  onSubmit,
  defaultValues,
  buttonLabel,
  children,
  methods,
  isLoading,
}: BackrsFormProps) => {
  const defaultMethods = useForm<BackrsFormValues>({ defaultValues });
  const methodsToUse = methods || defaultMethods;
  const { formState } = methodsToUse;

  const handleSubmit = methodsToUse.handleSubmit((data: BackrsFormValues) => {
    onSubmit(data);
  });

  // Group fields by the 'group' property
  const groupedFields: Record<string, BackrsFormField[]> = {};

  fields.forEach((field) => {
    const groupKey = field.group || `__unique_${field.name}`;

    if (!groupedFields[groupKey]) {
      groupedFields[groupKey] = [];
    }

    groupedFields[groupKey].push(field);
  });

  return (
    <FormProvider {...methodsToUse}>
      <Box component="form" onSubmit={handleSubmit} noValidate mt={1} width="100%">
        {Object.entries(groupedFields).map(([groupKey, group]) => {
          const isSingleFieldGroup = group.length === 1 && group[0].group === undefined;

          return (
            <Box
              key={groupKey}
              // If there is only one field in the group, display it as a block
              // if there are two or more in the group, we want to display them as a row
              display={isSingleFieldGroup ? 'block' : 'flex'}
              sx={isSingleFieldGroup ? {} : groupContainerStyle}
            >
              {group.map((field) => (
                <Fragment key={field.id}>
                  {field.divider && <Divider sx={{ my: 2 }} />}
                  <Controller
                    name={field.name}
                    control={methodsToUse.control}
                    rules={field.rules}
                    render={({ field: controllerField, fieldState: { error } }) => (
                      <BackrsTextField
                        id={field.name}
                        disabled={field.disabled}
                        name={field.name}
                        label={field.label}
                        type={field.type}
                        placeholder={field.placeholder}
                        icon={field.icon}
                        autoComplete={field.autoComplete}
                        autoFocus={field.autoFocus}
                        value={controllerField.value}
                        onChange={controllerField.onChange}
                        inputProps={field.inputProps}
                        sx={
                          {
                            ...defaultTextFieldStyle,
                            ...field.sx,
                          } as SxProps<Theme>
                        }
                        error={error?.message}
                      />
                    )}
                  />
                </Fragment>
              ))}
            </Box>
          );
        })}

        {children}
        {buttonLabel && (
          <Box sx={buttonContainerStyle}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={buttonStyle}
              disabled={!formState.isValid || isLoading}
              endIcon={
                isLoading ? (
                  <Box sx={loadingSpinnerStyle}>
                    <Lottie animationData={lottieAnimations.dotsDotting} loop />
                  </Box>
                ) : undefined
              }
            >
              {buttonLabel}
            </Button>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};

const loadingSpinnerStyle: SxProps<Theme> = {
  width: 20,
  height: 20,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const buttonStyle: SxProps<Theme> = {
  mx: 2,
  borderRadius: 6,
  textTransform: 'none',
  fontSize: '1.125rem',
  fontWeight: 700,
  lineHeight: '2rem',
  backgroundColor: appColors.color.navy2,
  boxShadow: 'none',
};

const buttonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: { xs: 3, sm: 8 },
};

const defaultTextFieldStyle: SxProps<Theme> = {
  mb: 1,
  backgroundColor: appColors.color.blue10,
  width: '100%',
};

const groupContainerStyle: SxProps<Theme> = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 2,
};
